import { RouteLocationNormalized, RouteMeta, Router } from 'vue-router'
import {
  AppNavigationGuard,
  AppRouteMeta,
  createMfWrapper,
} from '@sennder/shell-utilities'

import { authRoutes } from '@/modules/auth'
import { signupRoutes } from '@/modules/signup'
import { analytics } from '@/services/analyticsProvider'
import { getStateData } from '@/store/getters'
import router from '@/router'
import { logger } from '@/services/logger/loggers'
import { AppRoute, routes } from '@/config/routes'
import { getAuth0Token } from '@/services/tokenManager'
import { i18n } from '@/services/i18n'

export const initRouter = async () => {
  await registerRoutes()
  initRouteGuards(router)
}

const setPageTitle = (name: string, meta: RouteMeta): void => {
  // TODO: need to translate page titles in POEditor
  const pageName =
    (meta.title && i18n.global.t(meta.title)) ||
    name.charAt(0).toUpperCase() + name.slice(1)

  document.title = `${pageName} |  Orcas - sennder`
}

/**
 * Micro-frontends with routes
 */

export const addAppRoute = (routePath: AppRoute, routeMeta: AppRouteMeta) => {
  const component = async () =>
    createMfWrapper({
      id: routeMeta.name,
      renderer: routeMeta.renderer,
      hooks: {
        failure: logger.error,
      },
      router,
    })

  const guards = routeMeta.guards || []

  router.addRoute({
    path: routePath,
    name: routeMeta.name,
    component,
    meta: {
      public: routeMeta.public,
      fullscreenLayout: routeMeta.fullscreenLayout,
      guards,
      analytics: routeMeta.context?.analytics,

      title: `navigation.item.${routeMeta.name}`,
    },
    children: [
      {
        path: ':catchAll(.*)',
        name: routeMeta.name,
        meta: {
          guards,
        },
        component,
      },
    ],
  })
}

const registerRoutes = async () => {
  // Route path redirect
  router.addRoute({
    path: '/',
    redirect: { path: '/marketplace' },
    meta: {
      guards: [
        () => {
          // TODO: maybe better find first visible page in menu?
          if (getStateData().hasOnboardingFormAccess) {
            return { path: '/onboarding' }
          }
          return true
        },
      ],
    },
  })

  Object.entries(routes).forEach(([path, route]) => {
    addAppRoute(path as AppRoute, route)
  })

  for (const route of authRoutes) {
    router.addRoute(route)
  }

  for (const route of signupRoutes) {
    router.addRoute(route)
  }

  router.addRoute({
    path: '/:catchAll(.*)',
    name: 'not-found',
    meta: {
      public: true,
      fullscreenLayout: true,
    },
    component: () => import('../NotFound.vue'),
  })
}

const initRouteGuards = (router: Router) => {
  router.beforeEach((to, from) => {
    return routeNavigationGuard(to, from)
  })

  router.beforeResolve((to) => {
    const { name, meta } = to
    setPageTitle(String(name), meta)

    const PAGE_VIEW_EXCLUDE_COMPONENTS = ['marketplace', 'profile-mf-component']

    // TODO: remove automatic trackPage() calls when all mFs track their own pages
    const module = meta.npmName
    if (
      (!module || !PAGE_VIEW_EXCLUDE_COMPONENTS.includes(module)) &&
      meta.analytics
    ) {
      analytics.trackPage(meta.analytics.module, {
        module: meta.analytics.module,
      })
    }
  })
}

const isAuthenticatedGuard: AppNavigationGuard = async (
  to: RouteLocationNormalized
) => {
  const isAuthenticated = !!(await getAuth0Token())
  if (isAuthenticated) {
    return true
  } else {
    return {
      path: '/login',
      query: { redirectTo: encodeURIComponent(to.fullPath) },
    }
  }
}

export const routeNavigationGuard: AppNavigationGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) => {
  const authGuards = to.meta.public ? [] : [isAuthenticatedGuard]
  const guards = [...authGuards, ...(to.meta.guards || [])]
  if (guards.length === 0) {
    return true
  }

  for (const guard of guards) {
    const result = await guard(to, from)
    if (
      typeof result === 'object' ||
      typeof result === 'string' ||
      result === false
    ) {
      return result
    }
  }

  return true
}
